import xor from 'lodash/xor'
import { defineStore } from 'pinia'
import { useGraphqlFetch } from '~/composables/craft-fetch'
import { useCacheStore } from '~/store/cache'
import { usePageStore } from '~/store/page'
const hitsPerPage = 19

export const useArticleOverviewStore = defineStore('article-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const graphqlFetch = useGraphqlFetch()

  const store = {
    // Global results (i.e. unaffected by search context)
    allFacets: ref({}),

    // Search context
    categories: ref<string[]>([]),
    pageNumber: ref(1),
    site: ref('default'),
    siteGroup: ref('ddcf22b1-df04-4c71-88cb-3f4d54315708'),
    subjects: ref<string[]>([]),
    types: ref<string[]>([]),

    // Search results
    articles: ref<any[]>([]), // Define a proper type if possible
    facets: ref<Record<string, any>>({}), // Define a proper type if possible
    hits: ref<any[]>([]), // Define a proper type if possible
    totalHits: ref(0),
  }

  // Getters
  store.hitsPerPage = computed(() => hitsPerPage)
  store.urlParameters = computed(() => ({
    categories: store.categories.value,
    page: store.pageNumber.value === 1 ? undefined : store.pageNumber.value,
    subjects: store.subjects.value,
    types: store.types.value,
  }))
  store.getFacets = computed(() => {
    const allFacets = store.allFacets.value
    const facets = store.facets.value
    const categories = store.categories.value
    const subjects = store.subjects.value
    // const types = store.types.value

    const helper = (fromAllFacets, fromFacets, activeList) => {
      const currentCountMap = Object.fromEntries((fromFacets || []).map(({ count, label, value }) => [value || label, count]))
      return (fromAllFacets || []).map(({ value, label }) => ({
        active: activeList.includes(value || label),
        count: currentCountMap[value || label] || 0,
        label,
        value: value || label,
      }))
    }
    return [
      { name: 'categories', values: helper(allFacets.categories, facets.categories, categories) },
      { name: 'subjects', values: helper(allFacets.subjects, facets.subjects, subjects) },
      // DB-xxx: hide the types facet from the available facets list
      // { name: 'types', values: helper(allFacets.types, facets.types, types) },
    ]
  })
  store.activeFacets = computed(() => {
    const allFacets = store.allFacets.value
    const categories = store.categories.value
    const subjects = store.subjects.value
    const types = store.types.value

    const helper = (fromAllFacets, activeList, name) =>
      (fromAllFacets || []).filter(({ value, label }) => activeList.includes(value || label)).map(({ value, label }) => ({ name, label, value: value || label }))
    return [...helper(allFacets.subjects, subjects, 'subjects'), ...helper(allFacets.types, types, 'types'), ...helper(allFacets.categories, categories, 'categories')]
  })

  // Global results (i.e. unaffected by search context)
  function setAllFacets(allFacets) {
    store.allFacets.value = allFacets
  }

  function initialize({ site, siteGroup, categories, page, subjects, types }) {
    const toArray = (value) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    store.site.value = site
    store.siteGroup.value = siteGroup
    store.categories.value = toArray(categories)
    store.pageNumber.value = typeof page === 'string' ? Math.max(1, Number.parseInt(page)) : 1
    store.subjects.value = toArray(subjects)
    store.types.value = toArray(types)
  }

  function reset() {
    store.categories.value = []
    store.pageNumber.value = 1
    store.subjects.value = []
    store.types.value = []
    fetchArticles()
  }

  function toggleFacet({ name, value }) {
    switch (name) {
      case 'categories':
        store.categories.value = xor(store.categories.value, [value])
        store.pageNumber.value = 1
        fetchArticles()
        break
      case 'subjects':
        store.subjects.value = xor(store.subjects.value, [value])
        store.pageNumber.value = 1
        fetchArticles()
        break
      case 'types':
        store.types.value = xor(store.types.value, [value])
        store.pageNumber.value = 1
        fetchArticles()
        break
      default:
        console.error(`Unable to toggle unknown facet "${name}" with value "${value}"`)
        break
    }
  }

  function setPageNumber(pageNumber) {
    store.pageNumber.value = Math.max(1, pageNumber)
    fetchArticles()
  }

  /**
   * Search results
   * @internal
   */
  function setArticles(articles) {
    store.articles.value = articles
  }

  function setFacets(facets) {
    store.facets.value = facets
  }

  function setHits(hits) {
    store.hits.value = hits
  }

  function setTotalHits(totalHits) {
    store.totalHits.value = totalHits
  }

  async function fetchAllFacets() {
    const key = `article-overview;all-facets;site:${store.site.value};siteGroup:${store.siteGroup.value}`
    const ttl = 900

    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query allArticleFacets($site:[String], $siteGroup:[String]) {
            elasticSearch(site:$site, searchSiteGroupFacet: $siteGroup, section:"article") {
              facets(limit:1024) {
                categories: articleCategories { value, count }
                subjects: articleSubjects { value, count }
                types: articleType { value, count }
              }
            }
          }`,
        variables: {
          site:
            store.site.value === 'inspectiedienst'
              ? [store.site.value] // Only look in the subsite
              : ['default', store.site.value], // Always look on the corporate site to match Articles marked with the additional searchSiteGroup
          siteGroup: store.siteGroup.value,
        },
      })
      return elasticSearch.facets
    }

    // Fetch the hits
    const allFacets = await cacheStore.fetch({ key, ttl, fetcher })
    setAllFacets(allFacets)
    return allFacets
  }

  async function fetchArticles() {
    const key = [
      'article-overview',
      `site:${store.site.value}`,
      `siteGroup:${store.siteGroup.value}`,
      `categories:${store.categories.value.join(',')}`,
      `subjects:${store.subjects.value.join(',')}`,
      `types:${store.types.value.join(',')}`,
    ].join(';')
    const ttl = 1

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query searchArticles($site:[String], $siteGroup: [String], $categories:[String], $subjects:[String], $types:[String]) {
            elasticSearch(site:$site, searchSiteGroupFacet: $siteGroup, section:"article", articleCategoriesFacet:$categories, articleSubjectsFacet:$subjects, articleTypeFacet:$types, orderBy:"postDate DESC") {
              facets(limit:1024) {
                categories: articleCategories { value, count }
                subjects: articleSubjects { value, count }
                types: articleType { value, count }
              }
              count
              hits(limit:1024) {
               site: siteHandle
               uri
              }
            }
          }`,
        variables: {
          site:
            store.site.value === 'inspectiedienst'
              ? [store.site.value] // Only look in the subsite
              : ['default', store.site.value], // Always look on the corporate site to match Articles marked with the additional searchSiteGroup
          siteGroup: store.siteGroup.value,
          categories: store.categories.value,
          subjects: store.subjects.value,
          types: store.types.value,
        },
      })

      return {
        facets: elasticSearch.facets,
        hits: elasticSearch.hits,
        totalHits: elasticSearch.count,
      }
    }

    // Fetch the hits
    const { facets, hits, totalHits } = await cacheStore.fetch({ key, ttl, fetcher })
    setFacets(facets)
    setHits(hits)
    setTotalHits(totalHits)

    // Fetch all associated entries within the current page
    const slice = hits.slice((store.pageNumber.value - 1) * hitsPerPage, store.pageNumber.value * hitsPerPage)
    const articles = await Promise.all(slice.map((hit) => pageStore.fetchUri(hit)))
    setArticles(articles.filter((article) => article))
    return articles
  }

  return {
    ...store,
    initialize,
    reset,
    toggleFacet,
    setPageNumber,
    setFacets,
    setHits,
    setTotalHits,
    fetchAllFacets,
    fetch: fetchArticles,
  }
})
